import HackleCore from "../../HackleCore"
import { HackleUserManualOverrideStorage } from "../evaluation/service/experiment/match/ManualOverrideStorage"
import { Metrics } from "../metrics/Metrics"
import { Decision, ExperimentConfig, ExperimentType, FeatureFlagDecision, HackleUser, List, Long } from "../model/model"
import { UserManager } from "./UserManager"

export interface HackleUserExplorerBase {
  currentUser(): HackleUser

  getAbTestDecisions(): List<[ExperimentConfig, Decision]>

  getAbTestOverrides(): Map<Long, Long>

  setAbTestOverride(experiment: ExperimentConfig, variationId: Long): void

  resetAbTestOverride(experiment: ExperimentConfig): void

  resetAllAbTestOverride(): void

  getFeatureFlagDecisions(): List<[ExperimentConfig, FeatureFlagDecision]>

  getFeatureFlagOverrides(): Map<Long, Long>

  setFeatureFlagOverride(experiment: ExperimentConfig, variationId: Long): void

  resetFeatureFlagOverride(experiment: ExperimentConfig): void

  resetAllFeatureFlagOverride(): void
}

export class HackleUserExplorerImpl implements HackleUserExplorerBase {
  constructor(
    private core: HackleCore,
    private userManager: UserManager,
    private abOverrideStorage: HackleUserManualOverrideStorage,
    private ffOverrideStorage: HackleUserManualOverrideStorage
  ) {}

  currentUser(): HackleUser {
    return this.userManager.hackleUser()
  }

  // The exported devtools contract requires the full local config model. Remote mode never reaches
  // here because resolveDevTools() refuses to build an explorer for it; the filter below only
  // narrows the common Experiment type down to the config model the contract declares.
  getAbTestDecisions(): List<[ExperimentConfig, Decision]> {
    return this.core
      .experiments(this.currentUser())
      .entries()
      .filter((entry): entry is [ExperimentConfig, Decision] => entry[0] instanceof ExperimentConfig)
  }

  getAbTestOverrides(): Map<number, number> {
    return this.abOverrideStorage.getAll()
  }

  setAbTestOverride(experiment: ExperimentConfig, variationId: number): void {
    this.abOverrideStorage.set(experiment, variationId)
    this.increment("AB_TEST", "set")
  }

  resetAbTestOverride(experiment: ExperimentConfig): void {
    this.abOverrideStorage.remove(experiment)
    this.increment("AB_TEST", "reset")
  }

  resetAllAbTestOverride(): void {
    this.abOverrideStorage.clear()
    this.increment("AB_TEST", "reset.all")
  }

  getFeatureFlagDecisions(): List<[ExperimentConfig, FeatureFlagDecision]> {
    return this.core
      .featureFlags(this.currentUser())
      .entries()
      .filter((entry): entry is [ExperimentConfig, FeatureFlagDecision] => entry[0] instanceof ExperimentConfig)
  }

  getFeatureFlagOverrides(): Map<number, number> {
    return this.ffOverrideStorage.getAll()
  }

  setFeatureFlagOverride(experiment: ExperimentConfig, variationId: number): void {
    this.ffOverrideStorage.set(experiment, variationId)
    this.increment("FEATURE_FLAG", "set")
  }

  resetFeatureFlagOverride(experiment: ExperimentConfig): void {
    this.ffOverrideStorage.remove(experiment)
    this.increment("FEATURE_FLAG", "reset")
  }

  resetAllFeatureFlagOverride(): void {
    this.ffOverrideStorage.clear()
    this.increment("FEATURE_FLAG", "reset.all")
  }

  private increment(experimentType: ExperimentType, operation: string) {
    Metrics.counter("experiment.manual.override", {
      "experiment.type": experimentType,
      operation
    }).increment()
  }
}
